/* ★ v29.35（用户定案）：`dbViewConfig.vue` 按步骤拆分时，把 `view_meta` 的两端读写
   提成**纯函数**——它横跨「展示配置」步（表单里的 5 个键）与外壳（详情加载 / 提交），
   放在任一侧都会造成反向依赖，提成不持有状态的纯函数最干净。

   ★ v29.18：order_json（迁到列配置默认排序）/ max_page_size / pivot_table_max_records（机制废除）
   从 META_KEYS 移除——编辑态加载时存量值落入 preserved，原样保留，不再回写表单。 */

const META_KEYS = [
  'default_view_type',
  'enable_view_types',
  'is_pagination',
  'pageSize',
  'page_size_options',
];

/**
 * 详情加载：把 view_meta 拆成「表单消费的 5 个键」与「原样保留的其余键」。
 * 表单键**就地写进 model**（对象引用共享，外壳与各步骤看到的是同一个对象），
 * 其余键返回给调用方持有，提交时原样合并回去（btn_list、advanced_base_conditions 等不会丢）。
 */
export const applyViewMeta = (model: any, meta: Record<string, any>): Record<string, any> => {
  const preserved: Record<string, any> = {};
  Object.keys(meta || {}).forEach((k) => {
    if (!META_KEYS.includes(k)) preserved[k] = meta[k];
  });
  // ★ v29.13：org_filter 已废除（不再消费），存量键落入 preserved 原样保留
  model.default_view_type = meta.default_view_type ?? undefined;
  model.enable_view_types = Array.isArray(meta.enable_view_types) ? meta.enable_view_types : [];
  model.is_pagination = meta.is_pagination ?? true; // ★ v29.18：默认开启（与页面实际行为一致）
  model.pageSize = meta.pageSize ?? undefined;
  model.page_size_options = Array.isArray(meta.page_size_options) ? meta.page_size_options : [];
  return preserved;
};

/**
 * 提交：结构化值非空才写回，避免把「未配置」变成 null 脏数据。
 * ★ v29.13：org_filter 不再写回（引擎无消费点；存量键留在 preserved 里原样保留）。
 */
export const buildViewMeta = (model: any, preservedMeta: Record<string, any>) => {
  const meta: any = { ...preservedMeta };
  if (model.default_view_type) meta.default_view_type = model.default_view_type;
  else delete meta.default_view_type;
  if (model.enable_view_types && model.enable_view_types.length)
    meta.enable_view_types = model.enable_view_types;
  else delete meta.enable_view_types;
  /* ★ v29.18：default_view_type 必须在 enable_view_types 范围内（空 = 不限），
     不在范围内的配置视为无效，落库前剔除 */
  if (
    Array.isArray(model.enable_view_types) &&
    model.enable_view_types.length &&
    meta.default_view_type &&
    !model.enable_view_types.includes(meta.default_view_type)
  ) {
    delete meta.default_view_type;
  }
  if (typeof model.is_pagination === 'boolean') meta.is_pagination = model.is_pagination;
  else meta.is_pagination = true; // ★ v29.18：默认开启
  if (model.pageSize != null) meta.pageSize = model.pageSize;
  else delete meta.pageSize;
  if (model.page_size_options && model.page_size_options.length)
    meta.page_size_options = model.page_size_options;
  else delete meta.page_size_options;
  return meta;
};
